import { type FC } from 'react';
import classNames from 'classnames/bind';
import { FAIcon } from '../../Icons/Icons';
import { type TextAreaPropsType } from '../types/propsType';
import { TextAreaValidations } from '../types/enums';
import styles from './FloatLabelTextArea.module.scss';

/**
 * @description TextArea component provides <TextArea></TextArea> with basic styling;W
 * Props ->
 * className: prop to get custom styling class
 * label: prop to add field label above text area
 * status: defines status of entry if any i.e. default,valid or invalid
 * rows: specifies the visible number of lines in a text area
 * cols: specifies the visible width of a text area
 *
 */
const cx = classNames.bind(styles);
const FloatLabelTextArea: FC<TextAreaPropsType> = ({
  id = '',
  className = '',
  label = '',
  status,
  disabled = false,
  readOnly = false,
  resize = false,
  required = false,
  message = '',
  forwardedRef,
  rows = 5,
  isSensitiveField,
  ...restProps
}) => {
  const { VALID, INVALID } = TextAreaValidations;
  const currentIcon = status === VALID ? 'circle-check' : status === INVALID ? 'square-exclamation' : '';
  const configs = ConfigService.getConfigs();
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;
  const CClassname = cx('textarea', { [`textarea-validated-${status}`]: true });
  const CSubClassname = cx('textarea-field-container', {
    disabled: disabled,
    'read-only': readOnly,
    resize: !resize,
    'no-label': !label,
  });
  const CTextAreaClassname = cx('textarea-field', { 'without-value': !restProps?.value }, className);
  return (
    <div className={CClassname}>
      <div className={CSubClassname}>
        {label && (
          <label htmlFor={id} className={styles['float-textarea-label']}>
            {label} {required && <sup>*</sup>}
          </label>
        )}
        <textarea
          id={id}
          ref={forwardedRef}
          className={CTextAreaClassname}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
          {...restProps}
        />
      </div>
      <section className={styles['textarea-drawer']}>
        {currentIcon ? <FAIcon iconName={currentIcon} className={styles['icon']} /> : ''}
        {message && (
          <span
            role="presentation"
            aria-label="message"
            aria-details={`${label} input message`}
            className={styles['textarea-message']}
          >
            {message}
          </span>
        )}
      </section>
    </div>
  );
};
FloatLabelTextArea.displayName = 'FloatLabelTextArea';

export default FloatLabelTextArea;
